"use client";

import { useMemo, useRef } from "react";

import { shallowEqual } from "react-redux";

import { createGradeColorMap } from "@/app/(internal)/panel/(maps)/_constants/plotting-filters.constants";
import {
  createGradeOptions,
  createSchoolTypeOptions,
  createImportPeriodOptions,
  type PlottingGradeListboxOption,
} from "@/app/(internal)/panel/(maps)/_utils/plotting-filters.utils";
import type { ListboxFieldOption } from "@/components/flowbite/forms/fields/listbox/types";
import { useOnPathnameChange } from "@/hooks/use-on-pathname-change.hook";
import type { GradeColorMap } from "@/providers/maps/contexts/plotting-filters-context.types";
import districtsApi, {
  useGetDistrictMetadataQuery,
  useGetDistrictSchoolsQuery,
  useGetSchoolColorPaletteQuery,
} from "@/providers/store/api/districts/districts";
import type { IDistrictSchoolListItem } from "@/providers/store/api/districts/districts.interface";
import { useAppSelector } from "@/providers/store/hooks";

const PLOTTING_METADATA_POLLING_INTERVAL_MS = 30 * 60 * 1000;

export interface UsePlottingFiltersDataResult {
  selectedDistrictId: string | null;
  districtMetadata: ReturnType<typeof useGetDistrictMetadataQuery>["data"];
  schoolColorPalette: ReturnType<typeof useGetSchoolColorPaletteQuery>["data"];
  fulfilledTimeStamp: number | undefined;
  prevFulfilledTimeStamp: number | undefined;
  prevDistrictIdRef: React.MutableRefObject<string | null>;
  importPeriodOptions: ListboxFieldOption[];
  schoolTypeOptions: ListboxFieldOption[];
  gradeOptions: PlottingGradeListboxOption[];
  gradeColorMap: GradeColorMap;
  districtSchools: IDistrictSchoolListItem[];
  isLoadingMetadata: boolean;
  isLoadingColorPalette: boolean;
  hasStudentDataProcessing: boolean;
}

export function usePlottingFiltersData(): UsePlottingFiltersDataResult {
  const selectedDistrictId = useAppSelector(
    (s) => s.district.selectedDistrict?.id ?? null,
    shallowEqual,
  );

  const prevDistrictIdRef = useRef<string | null>(selectedDistrictId);

  useOnPathnameChange(() => {
    prevDistrictIdRef.current = null;
  });

  const metadataQueryArg = { districtId: selectedDistrictId ?? "" };
  const skipMetadataQuery = !selectedDistrictId || selectedDistrictId === "";

  const { data: cachedDistrictMetadata, fulfilledTimeStamp } =
    districtsApi.endpoints.getDistrictMetadata.useQueryState(metadataQueryArg, {
      skip: skipMetadataQuery,
    });

  const {
    data: districtMetadata,
    isLoading: isLoadingMetadata,
  } = useGetDistrictMetadataQuery(metadataQueryArg, {
    skip: skipMetadataQuery,
    pollingInterval: cachedDistrictMetadata?.hasStudentDataProcessing
      ? PLOTTING_METADATA_POLLING_INTERVAL_MS
      : 0,
  });

  const {
    data: schoolColorPalette,
    isLoading: isLoadingColorPalette,
  } = useGetSchoolColorPaletteQuery(
    { districtId: selectedDistrictId ?? "" },
    { skip: !selectedDistrictId || selectedDistrictId === "" },
  );

  const { data: districtSchoolsData } = useGetDistrictSchoolsQuery(
    { districtId: selectedDistrictId ?? "" },
    { skip: !selectedDistrictId || selectedDistrictId === "" },
  );

  const { fulfilledTimeStamp: prevFulfilledTimeStamp } =
    districtsApi.endpoints.getDistrictMetadata.useQueryState(
      { districtId: prevDistrictIdRef.current ?? "" },
      { skip: !prevDistrictIdRef.current || prevDistrictIdRef.current === "" },
    );

  const importPeriodOptions = useMemo<ListboxFieldOption[]>(() => {
    if (!selectedDistrictId || isLoadingMetadata || !fulfilledTimeStamp) return [];
    return createImportPeriodOptions(districtMetadata?.dateData);
  }, [districtMetadata, selectedDistrictId, isLoadingMetadata, fulfilledTimeStamp]);

  const schoolTypeOptions = useMemo<ListboxFieldOption[]>(() => {
    if (!selectedDistrictId || isLoadingMetadata || !fulfilledTimeStamp) return [];
    return createSchoolTypeOptions(districtMetadata?.schoolTypes);
  }, [districtMetadata, selectedDistrictId, isLoadingMetadata, fulfilledTimeStamp]);

  const gradeOptions = useMemo<PlottingGradeListboxOption[]>(() => {
    if (!selectedDistrictId || isLoadingMetadata || !fulfilledTimeStamp) return [];
    return createGradeOptions(districtMetadata?.studentGradeLevels ?? null);
  }, [districtMetadata, selectedDistrictId, isLoadingMetadata, fulfilledTimeStamp]);

  const gradeColorMap = useMemo<GradeColorMap>(() => {
    return createGradeColorMap();
  }, []);

  return {
    selectedDistrictId,
    districtMetadata,
    schoolColorPalette,
    fulfilledTimeStamp,
    prevFulfilledTimeStamp,
    prevDistrictIdRef,
    importPeriodOptions,
    schoolTypeOptions,
    gradeOptions,
    gradeColorMap,
    districtSchools: districtSchoolsData?.items ?? [],
    isLoadingMetadata,
    isLoadingColorPalette,
    hasStudentDataProcessing: districtMetadata?.hasStudentDataProcessing ?? false,
  };
}
